import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  getSpace,
  updatePolicy,
  updateSpace,
  listSpaceMembers,
  inviteSpaceMember,
  removeSpaceMember,
  listSpaceAgents,
  listAgents,
  inviteAgentToSpace,
  removeAgentFromSpace,
  createAgent,
  createAgentKey,
  api,
  SpacePolicyRow,
  SpaceMemberRow,
  SpaceAgentRow,
  AgentRow,
} from "../lib/api";
import { getCurrentUser } from "../lib/auth";

const ALL_AGENT_PERMISSIONS = ["read", "write", "commit"] as const;
const MEMBER_ROLES = ["viewer", "editor", "admin"] as const;

export default function SpaceSettingsPage() {
  const { username, spaceSlug } = useParams<{ username: string; spaceSlug: string }>();
  const currentUser = getCurrentUser<{ sub: string }>();

  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [spaceName, setSpaceName] = useState("");
  const [spaceDesc, setSpaceDesc] = useState("");
  const [infoSaving, setInfoSaving] = useState(false);
  const [infoDirty, setInfoDirty] = useState(false);

  const [agentInstructions, setAgentInstructions] = useState("");
  const [instrDirty, setInstrDirty] = useState(false);
  const [instrSaving, setInstrSaving] = useState(false);

  const [policy, setPolicy] = useState<SpacePolicyRow | null>(null);
  const [policyDraft, setPolicyDraft] = useState<SpacePolicyRow | null>(null);
  const [policyDirty, setPolicyDirty] = useState(false);
  const [policySaving, setPolicySaving] = useState(false);
  const [policyError, setPolicyError] = useState<string | null>(null);

  const [members, setMembers] = useState<SpaceMemberRow[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [inviteMemberOpen, setInviteMemberOpen] = useState(false);
  const [memberUsername, setMemberUsername] = useState("");
  const [memberRole, setMemberRole] = useState<string>("editor");
  const [memberInviting, setMemberInviting] = useState(false);
  const [memberInviteError, setMemberInviteError] = useState<string | null>(null);
  const [removeMemberTarget, setRemoveMemberTarget] = useState<SpaceMemberRow | null>(null);
  const [removingMember, setRemovingMember] = useState(false);

  const [spaceAgents, setSpaceAgents] = useState<SpaceAgentRow[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [inviteAgentOpen, setInviteAgentOpen] = useState(false);
  const [myAgents, setMyAgents] = useState<AgentRow[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [selectedPerms, setSelectedPerms] = useState<string[]>(["write"]);
  const [agentInviting, setAgentInviting] = useState(false);
  const [agentInviteError, setAgentInviteError] = useState<string | null>(null);
  const [removeAgentTarget, setRemoveAgentTarget] = useState<SpaceAgentRow | null>(null);
  const [removingAgent, setRemovingAgent] = useState(false);

  const [webhooks, setWebhooks] = useState<Array<{ id: string; url: string; events: string; created_at: string }>>([]);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [addingWebhook, setAddingWebhook] = useState(false);
  const [showAddWebhook, setShowAddWebhook] = useState(false);

  const [quickConnectOpen, setQuickConnectOpen] = useState(false);
  const [quickName, setQuickName] = useState("My Agent");
  const [quickConnecting, setQuickConnecting] = useState(false);
  const [quickConnectError, setQuickConnectError] = useState<string | null>(null);
  const [quickConnectKey, setQuickConnectKey] = useState<string | null>(null);
  const [promptCopied, setPromptCopied] = useState(false);

  useEffect(() => {
    if (!username || !spaceSlug) return;
    setLoading(true);
    getSpace(username, spaceSlug)
      .then((spaceData) => {
        setIsOwner(spaceData.owner_user_id === currentUser?.sub);
        setPolicy(spaceData.policy);
        setPolicyDraft(spaceData.policy);
        setAgentInstructions(spaceData.agent_instructions ?? "");
        setSpaceName(spaceData.name ?? "");
        setSpaceDesc(spaceData.description ?? "");
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [username, spaceSlug]);

  useEffect(() => {
    if (!isOwner || !username || !spaceSlug) return;
    setMembersLoading(true);
    setAgentsLoading(true);
    listSpaceMembers(username, spaceSlug)
      .then(setMembers)
      .catch(() => {})
      .finally(() => setMembersLoading(false));
    listSpaceAgents(username, spaceSlug)
      .then(setSpaceAgents)
      .catch(() => {})
      .finally(() => setAgentsLoading(false));
    api.get<any[]>(`/api/${username}/${spaceSlug}/webhooks`)
      .then(setWebhooks)
      .catch(() => {});
  }, [isOwner, username, spaceSlug]);

  async function handleSaveInfo() {
    if (!username || !spaceSlug) return;
    setInfoSaving(true);
    try {
      await updateSpace(username, spaceSlug, { name: spaceName, description: spaceDesc || undefined });
      setInfoDirty(false);
    } finally {
      setInfoSaving(false);
    }
  }

  async function handleSaveInstructions() {
    if (!username || !spaceSlug) return;
    setInstrSaving(true);
    try {
      await updateSpace(username, spaceSlug, { agent_instructions: agentInstructions });
      setInstrDirty(false);
    } finally {
      setInstrSaving(false);
    }
  }

  function updatePolicyDraft<K extends keyof SpacePolicyRow>(key: K, value: SpacePolicyRow[K]) {
    setPolicyDraft((prev) => prev ? { ...prev, [key]: value } : prev);
    setPolicyDirty(true);
    setPolicyError(null);
  }

  async function handleSavePolicy() {
    if (!username || !spaceSlug || !policyDraft) return;
    setPolicySaving(true);
    setPolicyError(null);
    try {
      const updated = await updatePolicy(username, spaceSlug, policyDraft);
      setPolicy(updated);
      setPolicyDraft(updated);
      setPolicyDirty(false);
    } catch (e) {
      setPolicyError(e instanceof Error ? e.message : "Failed to save policy");
    } finally {
      setPolicySaving(false);
    }
  }

  async function handleInviteMember(e: React.FormEvent) {
    e.preventDefault();
    if (!username || !spaceSlug) return;
    setMemberInviting(true);
    setMemberInviteError(null);
    try {
      await inviteSpaceMember(username, spaceSlug, memberUsername, memberRole);
      const updated = await listSpaceMembers(username, spaceSlug);
      setMembers(updated);
      setInviteMemberOpen(false);
      setMemberUsername("");
      setMemberRole("editor");
    } catch (e) {
      setMemberInviteError(e instanceof Error ? e.message : "Failed to invite");
    } finally {
      setMemberInviting(false);
    }
  }

  async function handleRemoveMember() {
    if (!removeMemberTarget || !username || !spaceSlug) return;
    setRemovingMember(true);
    try {
      await removeSpaceMember(username, spaceSlug, removeMemberTarget.id);
      setMembers((prev) => prev.filter((m) => m.id !== removeMemberTarget.id));
      setRemoveMemberTarget(null);
    } catch {
      setRemoveMemberTarget(null);
    } finally {
      setRemovingMember(false);
    }
  }

  async function openInviteAgent() {
    setAgentInviteError(null);
    setSelectedAgentId("");
    setSelectedPerms(["write"]);
    try {
      setMyAgents(await listAgents());
    } catch {
      setMyAgents([]);
    }
    setInviteAgentOpen(true);
  }

  function togglePerm(perm: string) {
    setSelectedPerms((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  }

  async function handleInviteAgent(e: React.FormEvent) {
    e.preventDefault();
    if (!username || !spaceSlug || !selectedAgentId) return;
    setAgentInviting(true);
    setAgentInviteError(null);
    try {
      await inviteAgentToSpace(username, spaceSlug, selectedAgentId, selectedPerms);
      setSpaceAgents(await listSpaceAgents(username, spaceSlug));
      if (policy && policy.write_draft_policy !== "allowlist_agents") {
        const updated = await updatePolicy(username, spaceSlug, { write_draft_policy: "allowlist_agents" });
        setPolicy(updated);
        setPolicyDraft(updated);
      }
      setInviteAgentOpen(false);
    } catch (e) {
      setAgentInviteError(e instanceof Error ? e.message : "Failed to invite agent");
    } finally {
      setAgentInviting(false);
    }
  }

  async function handleRemoveAgent() {
    if (!removeAgentTarget || !username || !spaceSlug) return;
    setRemovingAgent(true);
    try {
      await removeAgentFromSpace(username, spaceSlug, removeAgentTarget.agent_id);
      setSpaceAgents((prev) => prev.filter((a) => a.agent_id !== removeAgentTarget.agent_id));
      setRemoveAgentTarget(null);
    } catch {
      setRemoveAgentTarget(null);
    } finally {
      setRemovingAgent(false);
    }
  }

  async function handleQuickConnect(e: React.FormEvent) {
    e.preventDefault();
    if (!username || !spaceSlug || !quickName.trim()) return;
    setQuickConnecting(true);
    setQuickConnectError(null);
    try {
      const agent = await createAgent({ display_name: quickName.trim() });
      const keyResult = await createAgentKey(agent.id, `${spaceSlug} key`);
      await inviteAgentToSpace(username, spaceSlug, agent.id, ["read", "write", "commit"]);
      setSpaceAgents(await listSpaceAgents(username, spaceSlug));
      if (policy && policy.write_draft_policy !== "allowlist_agents") {
        const updated = await updatePolicy(username, spaceSlug, { write_draft_policy: "allowlist_agents" });
        setPolicy(updated);
        setPolicyDraft(updated);
      }
      setQuickConnectKey(keyResult.key);
    } catch (e) {
      setQuickConnectError(e instanceof Error ? e.message : "Failed");
    } finally {
      setQuickConnecting(false);
    }
  }

  function copyAgentPrompt(apiKey: string) {
    const apiBase = (import.meta.env.VITE_API_URL as string | undefined) || window.location.origin;
    const prompt = [
      `You have access to Agent Wiki. Use these endpoints:`,
      ``,
      `Read context:  GET ${apiBase}/api/${username}/${spaceSlug}/llms.txt`,
      `Write a page:  POST ${apiBase}/api/${username}/${spaceSlug}/pages`,
      `Authorization: Bearer ${apiKey}`,
      ``,
      `Start by fetching llms.txt to understand the space, then create or update pages as needed.`,
      `Page body JSON: { "slug": "page-slug", "title": "Title", "content": "# Markdown..." }`,
    ].join("\n");
    navigator.clipboard.writeText(prompt);
    setPromptCopied(true);
    setTimeout(() => setPromptCopied(false), 2000);
  }

  const roleBadge = (role: string) => {
    switch (role) {
      case "admin": return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300";
      case "editor": return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
      default: return "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";
    }
  };

  if (!isOwner) {
    return (
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-8 py-10">
          <p className="text-gray-500 dark:text-gray-400">You don't have permission to view settings for this space.</p>
        </div>
      </main>
    );
  }

  return (
    <>
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-8 py-10 space-y-12">

          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>

          {loading && (
            <div className="space-y-3 animate-pulse">
              {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-24 bg-gray-100 dark:bg-gray-800 rounded-lg" />)}
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg px-4 py-3 text-sm">{error}</div>
          )}

          {!loading && !error && (
            <>
              {/* Agent Access */}
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Agent Access</h2>
                  {spaceAgents.length > 0 && (
                    <button
                      onClick={openInviteAgent}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Invite Agent
                    </button>
                  )}
                </div>

                {agentsLoading && (
                  <div className="animate-pulse space-y-2">
                    {Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-12 bg-gray-100 dark:bg-gray-800 rounded-lg" />)}
                  </div>
                )}

                {!agentsLoading && spaceAgents.length === 0 && !quickConnectKey && (
                  <div className="border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 space-y-4">
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">🤖 Connect an agent</p>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Create a new agent and get an API key in one step, or invite an agent you've already created.
                      </p>
                    </div>
                    {!quickConnectOpen ? (
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={() => { setQuickConnectOpen(true); setQuickName("My Agent"); setQuickConnectError(null); }}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          Quick Setup — create &amp; connect →
                        </button>
                        <button
                          onClick={openInviteAgent}
                          className="px-4 py-2 border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 text-sm font-medium rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                        >
                          Invite existing agent
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleQuickConnect} className="space-y-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={quickName}
                            onChange={(e) => setQuickName(e.target.value)}
                            placeholder="Agent name"
                            required
                            className="flex-1 border border-blue-300 dark:border-blue-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            type="submit"
                            disabled={quickConnecting}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                          >
                            {quickConnecting ? "Setting up..." : "Create & Connect"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setQuickConnectOpen(false)}
                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            Cancel
                          </button>
                        </div>
                        {quickConnectError && <p className="text-red-600 dark:text-red-400 text-sm">{quickConnectError}</p>}
                      </form>
                    )}
                  </div>
                )}

                {!agentsLoading && quickConnectKey && (
                  <div className="border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-xl p-6 space-y-3">
                    <p className="text-sm font-medium text-green-800 dark:text-green-300">
                      ✓ Agent created and connected with read + write + commit permissions.
                    </p>
                    <div className="bg-white dark:bg-gray-900 border border-green-200 dark:border-green-800 rounded-lg p-3 space-y-1 font-mono text-xs text-gray-700 dark:text-gray-300">
                      <div><span className="text-blue-500">Read context:  </span>GET /api/{username}/{spaceSlug}/llms.txt</div>
                      <div><span className="text-blue-500">Write page:    </span>POST /api/{username}/{spaceSlug}/pages</div>
                      <div><span className="text-blue-500">Authorization: </span>Bearer {quickConnectKey}</div>
                    </div>
                    <p className="text-xs text-amber-600 dark:text-amber-400">⚠ Save this key — it won't be shown again.</p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => copyAgentPrompt(quickConnectKey)}
                        className="px-4 py-2 bg-gray-800 hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        {promptCopied ? "Copied!" : "Copy agent prompt"}
                      </button>
                      <button
                        onClick={() => setQuickConnectKey(null)}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 text-sm font-medium rounded-lg hover:border-gray-400 transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                )}

                {!agentsLoading && spaceAgents.length > 0 && (
                  <>
                    <div className="mb-3">
                      <button
                        onClick={() => {
                          const apiBase = (import.meta.env.VITE_API_URL as string | undefined) || window.location.origin;
                          const prompt = [
                            `You have access to Agent Wiki. Use these endpoints:`,
                            ``,
                            `Read context:  GET ${apiBase}/api/${username}/${spaceSlug}/llms.txt`,
                            `Write a page:  POST ${apiBase}/api/${username}/${spaceSlug}/pages`,
                            `Authorization: Bearer <YOUR_API_KEY>`,
                            ``,
                            `Start by fetching llms.txt to understand the space, then create or update pages as needed.`,
                            `Page body JSON: { "slug": "page-slug", "title": "Title", "content": "# Markdown..." }`,
                          ].join("\n");
                          navigator.clipboard.writeText(prompt);
                          setPromptCopied(true);
                          setTimeout(() => setPromptCopied(false), 2000);
                        }}
                        className="px-3 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 rounded-lg transition-colors"
                      >
                        {promptCopied ? "Copied!" : "Copy agent prompt"}
                      </button>
                    </div>
                    <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
                      {spaceAgents.map((sa) => (
                        <div key={sa.agent_id} className="flex items-center justify-between px-5 py-3 bg-white dark:bg-gray-900">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">{sa.display_name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{sa.permissions.join(", ")}</p>
                          </div>
                          <button
                            onClick={() => setRemoveAgentTarget(sa)}
                            className="px-3 py-1.5 text-xs font-medium border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </section>

              {/* Space Info */}
              <section>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Space Info</h2>
                <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-5 space-y-4 bg-white dark:bg-gray-900">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                    <input
                      type="text"
                      value={spaceName}
                      onChange={(e) => { setSpaceName(e.target.value); setInfoDirty(true); }}
                      className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                    <textarea
                      value={spaceDesc}
                      onChange={(e) => { setSpaceDesc(e.target.value); setInfoDirty(true); }}
                      rows={2}
                      placeholder="What is this space for? Agents use this to understand the purpose."
                      className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleSaveInfo}
                      disabled={!infoDirty || infoSaving}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      {infoSaving ? "Saving..." : "Save"}
                    </button>
                    {!infoDirty && spaceName && <span className="text-xs text-gray-400">Saved</span>}
                  </div>
                </div>
              </section>

              {/* Agent Instructions */}
              <section>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Agent Instructions</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Describe the purpose of this space and how agents should write in it. This text is included in the space's <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">llms.txt</code> for agent discovery.
                </p>
                <textarea
                  value={agentInstructions}
                  onChange={(e) => { setAgentInstructions(e.target.value); setInstrDirty(true); }}
                  rows={5}
                  placeholder={`e.g. This space documents our product decisions. Write concise summaries in the style of Amazon's working backwards documents. Always include a "Why" section.`}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg p-3 text-sm font-mono bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex items-center gap-3 mt-3">
                  <button
                    onClick={handleSaveInstructions}
                    disabled={!instrDirty || instrSaving}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {instrSaving ? "Saving..." : "Save Instructions"}
                  </button>
                  {!instrDirty && agentInstructions && <span className="text-xs text-gray-400">Saved</span>}
                  {agentInstructions && (
                    <a
                      href={`https://agent-wiki.sxg.workers.dev/api/${username}/${spaceSlug}/llms.txt`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      View llms.txt →
                    </a>
                  )}
                </div>
              </section>

              {/* Space Policy */}
              <section>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Space Policy</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Controls who can read, write, and publish pages in this space.
                </p>
                {policyDraft && (
                  <div className="border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 overflow-hidden">

                    {/* Policy rows */}
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">

                      {/* Read access */}
                      <div className="px-5 py-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Who can read pages</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                              {policyDraft.read_policy === "public" && "Anyone on the internet, no login required."}
                              {policyDraft.read_policy === "authenticated" && "Any logged-in user with an account."}
                              {policyDraft.read_policy === "allowlist" && "Only agents/members explicitly added to this space."}
                              {policyDraft.read_policy === "private" && "Only you and invited members."}
                            </p>
                          </div>
                          <select
                            value={policyDraft.read_policy}
                            onChange={(e) => updatePolicyDraft("read_policy", e.target.value)}
                            className="shrink-0 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="public">Public</option>
                            <option value="authenticated">Authenticated users</option>
                            <option value="allowlist">Allowlist only</option>
                            <option value="private">Private</option>
                          </select>
                        </div>
                      </div>

                      {/* Agent write access */}
                      <div className="px-5 py-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Who can write drafts</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                              {policyDraft.write_draft_policy === "allowlist_agents"
                                ? "Only agents you've explicitly invited (via Agent Access above)."
                                : "Only you (owner). No agents can write to this space."}
                            </p>
                          </div>
                          <select
                            value={policyDraft.write_draft_policy}
                            onChange={(e) => updatePolicyDraft("write_draft_policy", e.target.value)}
                            className="shrink-0 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="allowlist_agents">Invited agents</option>
                            <option value="owner_only">Owner only</option>
                          </select>
                        </div>
                      </div>

                      {/* Review mode */}
                      <div className="px-5 py-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Review mode</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                              {(policyDraft.approval_mode ?? "auto") === "auto"
                                ? "Agent drafts are published immediately — no review step."
                                : "Agent drafts go to your Reviews tab and wait for approval before publishing."}
                            </p>
                          </div>
                          <select
                            value={policyDraft.approval_mode ?? "auto"}
                            onChange={(e) => updatePolicyDraft("approval_mode", e.target.value)}
                            className="shrink-0 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="auto">Auto-publish</option>
                            <option value="strict">Require review</option>
                          </select>
                        </div>
                      </div>

                    </div>

                    {/* Effect summary */}
                    <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 px-5 py-3">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Current effect:</p>
                      <p className="text-xs text-gray-700 dark:text-gray-300">
                        {policyDraft.write_draft_policy === "owner_only"
                          ? "Agents cannot write to this space."
                          : (policyDraft.approval_mode ?? "auto") === "auto"
                          ? "Invited agents write drafts → published immediately (no review)."
                          : "Invited agents write drafts → wait in Reviews for your approval → then published."}
                        {" "}
                        {policyDraft.read_policy === "public" ? "Anyone can read." : policyDraft.read_policy === "private" ? "Only members can read." : ""}
                      </p>
                    </div>

                    {/* Save */}
                    <div className="border-t border-gray-100 dark:border-gray-800 px-5 py-4 flex items-center gap-3">
                      <button
                        onClick={handleSavePolicy}
                        disabled={!policyDirty || policySaving}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        {policySaving ? "Saving..." : "Save Policy"}
                      </button>
                      {!policyDirty && policy && <span className="text-xs text-gray-400 dark:text-gray-500">Saved</span>}
                      {policyError && <p className="text-red-600 dark:text-red-400 text-sm">{policyError}</p>}
                    </div>
                  </div>
                )}
              </section>

              {/* Members */}
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Members</h2>
                  <button
                    onClick={() => { setInviteMemberOpen(true); setMemberInviteError(null); setMemberUsername(""); setMemberRole("editor"); }}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Invite Member
                  </button>
                </div>
                {membersLoading && (
                  <div className="animate-pulse space-y-2">
                    {Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-12 bg-gray-100 dark:bg-gray-800 rounded-lg" />)}
                  </div>
                )}
                {!membersLoading && members.length === 0 && (
                  <p className="text-sm text-gray-400 dark:text-gray-500">No members yet. Invite collaborators by username.</p>
                )}
                {!membersLoading && members.length > 0 && (
                  <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
                    {members.map((m) => (
                      <div key={m.id} className="flex items-center justify-between px-5 py-3 bg-white dark:bg-gray-900">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">{m.username}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{m.display_name ?? m.email}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${roleBadge(m.role)}`}>{m.role}</span>
                          <button
                            onClick={() => setRemoveMemberTarget(m)}
                            className="px-3 py-1.5 text-xs font-medium border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Webhooks */}
              <section>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Webhooks</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Notify external services when pages are submitted or published</p>
                  </div>
                  <button onClick={() => setShowAddWebhook(true)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                    Add Webhook
                  </button>
                </div>
                {webhooks.length === 0 && <p className="text-sm text-gray-400 dark:text-gray-500">No webhooks configured.</p>}
                {webhooks.length > 0 && (
                  <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
                    {webhooks.map((wh) => (
                      <div key={wh.id} className="flex items-center justify-between px-5 py-3 bg-white dark:bg-gray-900">
                        <div>
                          <p className="font-mono text-sm text-gray-900 dark:text-gray-100 truncate max-w-xs">{wh.url}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Events: {wh.events}</p>
                        </div>
                        <button
                          onClick={async () => {
                            await api.delete(`/api/${username}/${spaceSlug}/webhooks/${wh.id}`);
                            setWebhooks((prev) => prev.filter((w) => w.id !== wh.id));
                          }}
                          className="px-3 py-1.5 text-xs font-medium border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </main>

      {/* Invite Member Modal */}
      {inviteMemberOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Invite Member</h2>
            <form onSubmit={handleInviteMember} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Username <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={memberUsername}
                  onChange={(e) => setMemberUsername(e.target.value)}
                  required
                  placeholder="e.g. alice"
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
                <select
                  value={memberRole}
                  onChange={(e) => setMemberRole(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {MEMBER_ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  viewer: read only · editor: read + write drafts · admin: + manage members
                </p>
              </div>
              {memberInviteError && <p className="text-red-600 dark:text-red-400 text-sm">{memberInviteError}</p>}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setInviteMemberOpen(false)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors">Cancel</button>
                <button type="submit" disabled={memberInviting} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                  {memberInviting ? "Inviting..." : "Invite"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Remove Member Confirm */}
      {removeMemberTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Remove Member</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Remove <span className="font-medium text-gray-900 dark:text-gray-100">{removeMemberTarget.username}</span> from this space?
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setRemoveMemberTarget(null)} disabled={removingMember} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 transition-colors">Cancel</button>
              <button onClick={handleRemoveMember} disabled={removingMember} className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                {removingMember ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Agent Modal */}
      {inviteAgentOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Invite Agent</h2>
            <form onSubmit={handleInviteAgent} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Agent <span className="text-red-500">*</span>
                </label>
                {myAgents.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    No agents found. <Link to="/agents" className="text-blue-600 underline">Create one first.</Link>
                  </p>
                ) : (
                  <select
                    value={selectedAgentId}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                    required
                    className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select an agent</option>
                    {myAgents.map((a) => (
                      <option key={a.id} value={a.id}>{a.display_name}</option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Permissions</label>
                <div className="flex gap-4">
                  {ALL_AGENT_PERMISSIONS.map((perm) => (
                    <label key={perm} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input type="checkbox" checked={selectedPerms.includes(perm)} onChange={() => togglePerm(perm)} className="rounded" />
                      {perm}
                    </label>
                  ))}
                </div>
              </div>
              {agentInviteError && <p className="text-red-600 dark:text-red-400 text-sm">{agentInviteError}</p>}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setInviteAgentOpen(false)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 transition-colors">Cancel</button>
                <button type="submit" disabled={agentInviting || myAgents.length === 0} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                  {agentInviting ? "Inviting..." : "Invite"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Remove Agent Confirm */}
      {removeAgentTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Remove Agent</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Remove <span className="font-medium text-gray-900 dark:text-gray-100">{removeAgentTarget.display_name}</span> from this space?
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setRemoveAgentTarget(null)} disabled={removingAgent} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 transition-colors">Cancel</button>
              <button onClick={handleRemoveAgent} disabled={removingAgent} className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                {removingAgent ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Webhook Modal */}
      {showAddWebhook && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Add Webhook</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">URL <span className="text-red-500">*</span></label>
                <input type="url" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://..." className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Secret (optional)</label>
                <input type="text" value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)} placeholder="Leave blank to auto-generate" className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Triggers on: page submitted for review, page published. Signed with HMAC-SHA256.</p>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowAddWebhook(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Cancel</button>
              <button
                disabled={!webhookUrl || addingWebhook}
                onClick={async () => {
                  setAddingWebhook(true);
                  try {
                    const wh = await api.post<any>(`/api/${username}/${spaceSlug}/webhooks`, {
                      url: webhookUrl,
                      secret: webhookSecret || undefined,
                    });
                    setWebhooks((prev) => [...prev, wh]);
                    setShowAddWebhook(false);
                    setWebhookUrl("");
                    setWebhookSecret("");
                  } finally {
                    setAddingWebhook(false);
                  }
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {addingWebhook ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
